import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { LogOut, Menu, X, HelpCircle } from 'lucide-react';
import axios from 'axios';
import QuickStartGuide from './QuickStartGuide';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Navbar({ user, onLogout }) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState({
    unread_messages: 0,
    weight_log_pending: false
  });
  
  const isActive = (path) => location.pathname === path;
  
  // Fetch notifications for client or coach
  useEffect(() => {
    if (user) {
      const fetchNotifications = async () => {
        try {
          // Add timestamp to prevent caching
          const response = await axios.get(`${API}/notifications?t=${Date.now()}`);
          setNotifications(response.data);
        } catch (error) {
          console.error('Failed to fetch notifications:', error);
        }
      };
      
      fetchNotifications();
      // Refresh every 10 seconds (faster updates for coach notifications)
      const interval = setInterval(fetchNotifications, 10000);
      return () => clearInterval(interval);
    }
  }, [user]);
  
  const navLinks = user?.role === 'coach' ? [
    { path: '/coach', label: 'Dashboard' },
    { path: '/coach/clients', label: 'Clients' },
    { path: '/coach/weekly-reviews', label: 'Weekly Reviews' },
    { path: '/coach/recipe-library', label: 'Recipe Library' },
    { path: '/coach/food-bank', label: 'Food Bank' },
    { path: '/coach/finance', label: 'Finance' },
    { path: '/coach/messages', label: 'Messages' },
    { path: '/coach/cancellation-feedback', label: 'Feedback' },
    { path: '/coach/recipe-reports', label: 'Reports' },
    { path: '/coach/profile', label: 'Profile' }
  ] : [
    { path: '/app', label: 'Dashboard' },
    { path: '/app/meal-plan', label: 'Meal Plan' },
    { path: '/app/checkin', label: 'Check-In' },
    { path: '/app/weight-log', label: 'Weight Log' },
    { path: '/app/progress', label: 'Progress' },
    { path: '/app/messages', label: 'Messages' },
    { path: '/app/profile', label: 'Profile' }
  ];

  // Quick Start Guide toggle
  const [showQuickStart, setShowQuickStart] = useState(false);

  const handleLinkClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <nav className="navbar" data-testid="navbar">
      <div className="navbar-container">
        <div className="navbar-brand">
          <img src="/logo.png" alt="MealGuide" className="h-12" />
        </div>
        
        {/* Hamburger Menu Button - Mobile Only */}
        <button
          className="md:hidden flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-[#1a61e8] hover:bg-[#1a61e8]/10 transition-colors"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-button"
        >
          {mobileMenuOpen ? (
            <X className="h-6 w-6" />
          ) : (
            <Menu className="h-6 w-6" />
          )}
        </button>

        {/* Desktop Navigation - Hidden on Mobile */}
        <div className="hidden md:flex md:items-center md:gap-6">
          {navLinks.map(link => (
            <Link
              key={link.path}
              to={link.path}
              className={`nav-link ${isActive(link.path) ? 'active' : ''} relative`}
              data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
            >
              {link.label}
              
              {/* Client Badges */}
              {user?.role === 'client' && (
                <>
                  {/* Messages badge */}
                  {link.label === 'Messages' && notifications.unread_messages > 0 && (
                    <span className="absolute -top-1 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      {notifications.unread_messages}
                    </span>
                  )}
                  {/* Weight Log badge */}
                  {link.label === 'Weight Log' && notifications.weight_log_pending && (
                    <span className="absolute -top-1 -right-1 bg-red-500 rounded-full h-2.5 w-2.5"></span>
                  )}
                </>
              )}
              
              {/* Coach Badges */}
              {user?.role === 'coach' && (
                <>
                  {/* Messages badge */}
                  {link.label === 'Messages' && notifications.unread_messages > 0 && (
                    <span className="absolute -top-1 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      {notifications.unread_messages}
                    </span>
                  )}
                  {/* Weekly Reviews badge */}
                  {link.label === 'Weekly Reviews' && notifications.pending_reviews > 0 && (
                    <span className="absolute -top-1 -right-2 bg-orange-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      {notifications.pending_reviews}
                    </span>
                  )}
                  {/* Recipe Reports badge */}
                  {link.label === 'Reports' && notifications.recipe_reports > 0 && (
                    <span className="absolute -top-1 -right-2 bg-blue-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      {notifications.recipe_reports}
                    </span>
                  )}
                </>
              )}
            </Link>
          ))}
          
          {/* Quick Start Guide Button (Client only) - Desktop */}
          {user?.role === 'client' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowQuickStart(true)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              title="Quick Start Guide"
            >
              <HelpCircle className="h-4 w-4" />
              <span className="hidden lg:inline">Quick Start</span>
            </Button>
          )}
          
          <div className="flex items-center gap-2 ml-4">
            <span className="text-sm font-medium text-gray-700">
              {user?.first_name}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={onLogout}
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="md:hidden mobile-menu-dropdown" data-testid="mobile-menu-dropdown">
          <div className="flex flex-col space-y-1 p-4">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link-mobile ${isActive(link.path) ? 'active' : ''} relative flex items-center`}
                onClick={handleLinkClick}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
                
                {/* Client Badges */}
                {user?.role === 'client' && (
                  <>
                    {/* Messages badge */}
                    {link.label === 'Messages' && notifications.unread_messages > 0 && (
                      <span className="ml-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                        {notifications.unread_messages}
                      </span>
                    )}
                    {/* Weight Log badge */}
                    {link.label === 'Weight Log' && notifications.weight_log_pending && (
                      <span className="ml-2 bg-red-500 rounded-full h-2.5 w-2.5"></span>
                    )}
                  </>
                )}
                
                {/* Coach Badges */}
                {user?.role === 'coach' && (
                  <>
                    {/* Messages badge */}
                    {link.label === 'Messages' && notifications.unread_messages > 0 && (
                      <span className="ml-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                        {notifications.unread_messages}
                      </span>
                    )}
                    {/* Weekly Reviews badge */}
                    {link.label === 'Weekly Reviews' && notifications.pending_reviews > 0 && (
                      <span className="ml-2 bg-orange-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                        {notifications.pending_reviews}
                      </span>
                    )}
                    {/* Recipe Reports badge */}
                    {link.label === 'Reports' && notifications.recipe_reports > 0 && (
                      <span className="ml-2 bg-blue-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                        {notifications.recipe_reports}
                      </span>
                    )}
                  </>
                )}
              </Link>
            ))}

            {/* Quick Start Guide Button (Client only) */}
            {user?.role === 'client' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowQuickStart(true)}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                title="Quick Start Guide"
              >
                <HelpCircle className="h-4 w-4" />
                <span className="hidden lg:inline">Quick Start</span>
              </Button>
            )}

            {/* Logout Button */}
            
            <div className="pt-4 mt-4 border-t border-gray-200 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">
                {user?.first_name}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  handleLinkClick();
                  onLogout();
                }}
                data-testid="mobile-logout-button"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>

          </div>
        </div>
      )}

      {/* Quick Start Guide Modal - Available for both desktop and mobile */}
      <QuickStartGuide
        isOpen={showQuickStart}
        onClose={() => setShowQuickStart(false)}
        autoShow={false}
      />
    </nav>
  );
}
